import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import Axios from '../../api/axios';
import { asArray, filterArray } from '../../utils/arrayData.js';
import { filterKullanicilarByStatu, PARA_KUL_STATU } from '../../utils/kulStatu';
import { getCached, setCached, clearCached, casRefCacheKey, carRefCacheKey } from '../../utils/requestCache.js';
import DataGrid, { Paging, Column, LoadPanel, Scrolling, Lookup, Toolbar as GridToolbar, Item as GridToolbarItem } from '../BootstrapDataGrid';
import { Button } from '../BootstrapControls';
import Form, { GroupItem, SimpleItem, Label, RequiredRule } from '../BootstrapForm';
import notify from '../../utils/notifyUtil';
import Dict from '../../messages.js';
import * as glb from '../../globals.js';
import { emitCariSync, subscribeCariSync } from '../../utils/cariSyncBus.js';
import { injectQuickDatePresets } from '../../utils/dateQuickPresets';
import { normalizeKeysToUpper } from '../../utils/dbKeyNormalize.js';
import {
    getIadeOdemeKaynakBlockMessage,
    computeParaSummaryForSatisGrid,
} from '../../utils/paraIadeLimit.js';
import { attachFormEnterToNextField } from '../../utils/formEnterFocusNext';
import { gridHeightByRowCount, gridMaxHeightForRows } from '../../utils/gridAutoHeight.js';
import { canDeleteByOwner, tahsilatIsYapanRef } from '../../utils/ownRecordDelete.js';
import { allowedSatisYapanRefs, canAssignSatisYapan } from '../../utils/cariOwnerAccess.js';
import { KULLANICI_LKP_EVENT } from '../../utils/kullaniciLkp.js';
import { addMaxLengthToEditorOptions, addMaxLengthToGridColumn } from '../../utils/formValidationHelper.js';

const PARA_BORC_ALACAK_EPS = 0.005;

function normalizeParaUserSelections(row) {
    if (!row) return row;
    const next = { ...row };
    ['CP_MUS_TEM', 'CP_EKIP', 'CP_GORUSEN', 'CP_REKLAMCI', 'CP_HEMSIRE'].forEach((key) => {
        if (!Number(next[key])) next[key] = null;
    });
    return next;
}

export default function ParaPanel({ carRef, casRef, satisTarihi = null, paraReadOnly, refreshToken = 0, dataSahibiKulRef = null, gridHeight = null, gridVisibleRowCount = 5 }) {
    const [, setKullaniciLookupVersion] = useState(0);
    const [paraList, setParaList] = useState([]);
    const [activePara, setActivePara] = useState(null);
    const [formReadOnly, setFormReadOnly] = useState(true);
    const [formDirty, setFormDirty] = useState(false);
    const [newFormToken, setNewFormToken] = useState(0);
    const [focusedParaKey, setFocusedParaKey] = useState(null);
    const [isCreatingNew, setIsCreatingNew] = useState(false);
    const [paraSaving, setParaSaving] = useState(false);
    const paraSavingRef = useRef(false);
    const paraListRef = useRef([]);
    const loadedCasRefRef = useRef(null);
    const currentCasRef = useRef(casRef);
    currentCasRef.current = casRef;
    const createLockRef = useRef(false);
    const gridRef = useRef(null);
    const lastReqIdRef = useRef(0);
    const enterNavCleanupRef = useRef(null);
    const replaceParaList = useCallback((rows) => {
        paraListRef.current = rows;
        setParaList(rows);
    }, []);
    useEffect(() => {
        const refresh = () => setKullaniciLookupVersion((version) => version + 1);
        window.addEventListener(KULLANICI_LKP_EVENT, refresh);
        return () => window.removeEventListener(KULLANICI_LKP_EVENT, refresh);
    }, []);
    const attachEnterNavRoot = useCallback((node) => {
        if (enterNavCleanupRef.current) {
            enterNavCleanupRef.current();
            enterNavCleanupRef.current = null;
        }
        if (node) {
            enterNavCleanupRef.current = attachFormEnterToNextField(node);
        }
    }, []);
    useEffect(() => () => {
        if (enterNavCleanupRef.current) {
            enterNavCleanupRef.current();
            enterNavCleanupRef.current = null;
        }
    }, []);
    const formatMoney = useCallback((v) => (Number(v) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), []);
    const paraTurList = React.useMemo(
        () => filterArray(glb.default.sabitLkp, x => x.ST_TUR === 128),
        []
    );

    const paraTurListSelectable = React.useMemo(() => {
        // "Anlaşma Tutarı" satırı satıştan otomatik gelir; Para ekranında manuel seçilmesin.
        return (paraTurList || []).filter((t) => {
            const ad = String(t?.ST_AD || '').toLocaleLowerCase('tr-TR');
            return !(ad.includes('anlaş') || ad.includes('anlas'));
        });
    }, [paraTurList]);
    const resolveBorcluByTur = React.useCallback((cpTur) => {
        const tur = paraTurList.find(x => Number(x.ST_KODINT) === Number(cpTur));
        const ad = String(tur?.ST_AD || '').toLocaleLowerCase('tr-TR');
        if (ad.includes('anlaş') || ad.includes('anlas') || ad.includes('iade')) return true; // Anlaşma/iade -> Borç
        return false; // Diğerleri -> Alınan
    }, [paraTurList]);
    const isAgreementTur = React.useCallback((cpTur) => {
        const tur = paraTurList.find(x => Number(x.ST_KODINT) === Number(cpTur));
        const ad = String(tur?.ST_AD || '').toLocaleLowerCase('tr-TR');
        return ad.includes('anlaş') || ad.includes('anlas');
    }, [paraTurList]);
    const getTurAdForRow = React.useCallback((cpTur) => {
        const tur = paraTurList.find(x => Number(x.ST_KODINT) === Number(cpTur));
        return tur?.ST_AD || '';
    }, [paraTurList]);
    const applyParaChanges = useCallback((baseList, changes = {}) => {
        const toNum = (v) => Number(v || 0);
        const paraRef = (row) => toNum(row?.CP_REF ?? row?.cp_ref ?? row);
        const inserted = Array.isArray(changes.inserted) ? changes.inserted : [];
        const updated = Array.isArray(changes.updated) ? changes.updated : [];
        const deleted = Array.isArray(changes.deleted) ? changes.deleted : [];
        const deletedSet = new Set(deleted.map((x) => paraRef(x)));
        let next = (Array.isArray(baseList) ? baseList : []).filter((row) => !deletedSet.has(paraRef(row)));
        const upsert = (row) => {
            if (!row || typeof row !== 'object') return;
            const patch = normalizeKeysToUpper(row);
            const id = paraRef(patch);
            if (!id) return;
            const idx = next.findIndex((r) => paraRef(r) === id);
            if (idx >= 0) {
                next[idx] = { ...next[idx], ...patch };
            } else {
                next.unshift(patch);
            }
        };
        inserted.forEach(upsert);
        updated.forEach(upsert);
        next = next.sort((a, b) => paraRef(b) - paraRef(a));
        return next;
    }, []);
    const computeParaAggregate = useCallback(
        (list) => computeParaSummaryForSatisGrid(list, getTurAdForRow),
        [getTurAdForRow]
    );
    const applySatisSummaryToCache = useCallback((summary) => {
        if (!carRef || !casRef) return;
        const satisCacheKey = carRefCacheKey('SATIS_LST', carRef);
        const cachedSatis = getCached(satisCacheKey);
        if (!Array.isArray(cachedSatis)) return;
        const nextSatis = [...cachedSatis];
        const idx = nextSatis.findIndex((r) => Number(r?.CAS_REF) === Number(casRef));
        if (idx >= 0) {
            nextSatis[idx] = {
                ...nextSatis[idx],
                ALACAK: Number(summary?.ALACAK || 0),
                TOPLAM_ALINAN: Number(summary?.TOPLAM_ALINAN || 0),
                KALAN: Number(summary?.KALAN || 0)
            };
            setCached(satisCacheKey, nextSatis);
        }
    }, [carRef, casRef]);

    const applyParaListSnapshot = useCallback((rows, writeCache = true) => {
        const next = (Array.isArray(rows) ? rows : []).map(normalizeKeysToUpper);
        loadedCasRefRef.current = Number(casRef);
        replaceParaList(next);
        if (writeCache && casRef) setCached(casRefCacheKey('PARALIST', casRef), next);
        return next;
    }, [casRef, replaceParaList]);

    const fetchParaList = useCallback((forceReload = false) => {
        const targetCasRef = casRef;
        const myReqId = ++lastReqIdRef.current;
        if (!targetCasRef || targetCasRef <= 0) { loadedCasRefRef.current = null; replaceParaList([]); return; }
        const cacheKey = casRefCacheKey('PARALIST', targetCasRef);
        if (forceReload) {
            clearCached(cacheKey);
        }
        const cached = forceReload ? null : getCached(cacheKey);
        if (cached && Array.isArray(cached)) {
            if (myReqId !== lastReqIdRef.current) return;
            applyParaListSnapshot(cached, false);
            return;
        }
        Axios.get(`${process.env.REACT_APP_BACKEND_SERVER}CARI_SATIS/paraList?CAS_REF=${targetCasRef}`)
            .then(res => {
                if (myReqId !== lastReqIdRef.current) return;
                applyParaListSnapshot(res.data);
            })
            .catch(err => { if (myReqId !== lastReqIdRef.current) return; notify(Dict.get('serverError') + err, 'error', 2000); });
    }, [casRef, replaceParaList, applyParaListSnapshot]);

    useEffect(() => {
        loadedCasRefRef.current = null;
        replaceParaList([]);
        setActivePara(null);
        setFormReadOnly(true); setFormDirty(false); setIsCreatingNew(false); createLockRef.current = false;
    }, [casRef, replaceParaList]);

    useEffect(() => {
        if (refreshToken > 0 && casRef) {
            clearCached(casRefCacheKey('PARALIST', casRef));
        }
        fetchParaList();
        return () => { ++lastReqIdRef.current; };
    }, [fetchParaList, refreshToken]);
    useEffect(() => {
        const unsub = subscribeCariSync((evt) => {
            if (evt?.origin === 'para-panel') return;
            if (!carRef || Number(evt?.carRef) !== Number(carRef)) return;
            if (evt?.source === 'satis' && Number(evt?.casRef) === Number(casRef)) {
                // Kaydetten önce başlayan GET yeni anlaşma satırını geri almamalı.
                ++lastReqIdRef.current;
                const agreementRow = evt?.autoAgreement?.row;
                const agrRef = agreementRow ? Number(agreementRow?.CP_REF ?? agreementRow?.cp_ref ?? 0) : 0;
                if (Number(evt?.deletedCasRef) === Number(casRef)) {
                    applyParaListSnapshot([]);
                    setActivePara(null);
                } else if (agreementRow && agrRef > 0 && loadedCasRefRef.current === Number(casRef)) {
                    applyParaListSnapshot(applyParaChanges(paraListRef.current, { updated: [agreementRow] }));
                } else {
                    // Henüz tam liste yoksa yalnız anlaşmayı tam liste diye önbelleğe alma.
                    fetchParaList(true);
                }
            }
            if (evt?.source === 'para' && Number(evt?.casRef) === Number(casRef) && Array.isArray(evt?.paraCasList)) {
                ++lastReqIdRef.current;
                applyParaListSnapshot(evt.paraCasList);
            }
        });
        return () => unsub();
    }, [carRef, casRef, applyParaChanges, applyParaListSnapshot, fetchParaList, replaceParaList]);
    useEffect(() => {
        if (!formReadOnly || isCreatingNew || createLockRef.current) return;
        if (!Array.isArray(paraList) || paraList.length === 0) {
            setActivePara(null);
            setFocusedParaKey(null);
            return;
        }
        const selected = normalizeParaUserSelections(
            paraList.find((row) => Number(row.CP_REF) === Number(activePara?.CP_REF)) || paraList[0]
        );
        if (!activePara || Object.keys(selected).some((key) => selected[key] !== activePara[key])
            || Object.keys(activePara).some((key) => !(key in selected))) {
            setActivePara(selected);
            setFocusedParaKey(selected.CP_REF ?? null);
            setFormReadOnly(true);
            setFormDirty(false);
        }
    }, [paraList, activePara, formReadOnly, isCreatingNew]);

    const savePara = async (changeGroups) => {
        if (paraSavingRef.current) return false;
        if (!(Number(casRef) > 0) || loadedCasRefRef.current !== Number(casRef)) {
            notify('Borç/Alınan listesi henüz yüklenmedi. Listeyi yenileyip tekrar deneyiniz.', 'warning', 3500);
            return false;
        }
        paraSavingRef.current = true;
        setParaSaving(true);
        try {
            const projected = applyParaChanges(paraListRef.current, changeGroups);
            const iadeBlockMsg = getIadeOdemeKaynakBlockMessage(projected, getTurAdForRow);
            if (iadeBlockMsg) {
                notify(iadeBlockMsg, 'warning', 7000);
                return false;
            }
            let borcTop = 0;
            let alinanTop = 0;
            for (const r of projected) {
                const tut = Number(r?.CP_TUTAR) || 0;
                if (r?.CP_BA === true || r?.CP_BA === 1 || r?.CP_BA === '1') borcTop += tut;
                else if (r?.CP_BA === false || r?.CP_BA === 0 || r?.CP_BA === '0') alinanTop += tut;
            }
            if (alinanTop > borcTop + PARA_BORC_ALACAK_EPS) {
                notify(
                    'Tahsilat ve diğer alınan tutarların toplamı, borç / anlaşma tutarlarının toplamını geçemez. Müşteri alacaklı duruma düşülemez.',
                    'error',
                    7000,
                );
                return false;
            }
            const payload = { ...changeGroups, casRef, carRef, kulRef: glb.default.KULLANICI.KUL_REF };
            const res = await Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}CARI_SATIS/parakaydet?cid=${glb.default.KULLANICI.KUL_DEF_FIR_REF}`, payload);
            if (res.data && res.data.success) {
                const isCurrentSale = Number(currentCasRef.current) === Number(casRef);
                if (!isCurrentSale || loadedCasRefRef.current !== Number(casRef)) {
                    clearCached(casRefCacheKey('PARALIST', casRef));
                    clearCached(carRefCacheKey('PARA_LIST_BY_CAR', carRef));
                    clearCached(carRefCacheKey('SATIS_LST', carRef));
                    emitCariSync({ carRef, casRef, source: 'para', origin: 'para-panel' });
                    if (isCurrentSale) fetchParaList(true);
                    return false;
                }
                ++lastReqIdRef.current;
                const insertedRows = Array.isArray(res?.data?.inserted) ? res.data.inserted : [];
                const updatedRows = Array.isArray(res?.data?.updated) ? res.data.updated : [];
                const deletedRows = Array.isArray(res?.data?.deleted) ? res.data.deleted : [];
                const nextParaList = applyParaChanges(paraListRef.current, {
                    inserted: insertedRows,
                    updated: updatedRows,
                    deleted: deletedRows
                });
                replaceParaList(nextParaList);
                if (casRef) {
                    setCached(casRefCacheKey('PARALIST', casRef), nextParaList);
                    clearCached(casRefCacheKey('ISLEMLIST_LOOKUP', casRef));
                }
                const currentActiveRef = Number(activePara?.CP_REF || 0);
                const deletedSet = new Set(deletedRows.map((x) => Number(x?.CP_REF ?? x?.cp_ref ?? x)));
                if (deletedSet.has(currentActiveRef)) {
                    const first = nextParaList[0] || null;
                    setActivePara(first);
                    setFocusedParaKey(first?.CP_REF ?? null);
                } else {
                    const latest = insertedRows[0] || updatedRows.find((r) => Number(r?.CP_REF) === currentActiveRef) || null;
                    if (latest) {
                        setActivePara(latest);
                        setFocusedParaKey(latest?.CP_REF ?? null);
                    }
                }
                const summary = computeParaAggregate(nextParaList);
                applySatisSummaryToCache(summary);
                if (carRef) {
                    const paraByCarKey = carRefCacheKey('PARA_LIST_BY_CAR', carRef);
                    const cachedByCar = getCached(paraByCarKey);
                    if (Array.isArray(cachedByCar)) {
                        setCached(paraByCarKey, applyParaChanges(cachedByCar, {
                            inserted: insertedRows,
                            updated: updatedRows,
                            deleted: deletedRows
                        }));
                    }
                }
                notify(Dict.get('Kaydedildi'), 'success', 1500);
                emitCariSync({
                    carRef,
                    casRef,
                    source: 'para',
                    origin: 'para-panel',
                    paraChanges: { inserted: insertedRows, updated: updatedRows, deleted: deletedRows },
                    paraCasList: nextParaList,
                    paraSummary: summary
                });
                return true;
            } else {
                notify(Dict.get('serverError'), 'error', 2000);
                return false;
            }
        } catch (err) {
            const data = err?.response?.data;
            const apiMsg = data?.message;
            if (data?.error === 'PARA_IADE_LIMIT' && apiMsg) {
                notify(apiMsg, 'warning', 7000);
            } else {
                notify(apiMsg || (Dict.get('serverError') + err), 'error', 3500);
            }
            return false;
        } finally {
            paraSavingRef.current = false;
            setParaSaving(false);
        }
    };

    const isNewUnsavedRecord = !casRef || casRef === -1 || casRef <= 0;
    const isParaListLoaded = !isNewUnsavedRecord && loadedCasRefRef.current === Number(casRef);
    const isFormRecordNew = !activePara || Number(activePara.CP_REF) <= 0;
    const isActiveAgreement = !!activePara && isAgreementTur(activePara.CP_TUR);
    const isGeneralYetkiUser = Number(glb.default?.KULLANICI?.KUL_YETKI) === 0
        || Number(glb.default?.KULLANICI?.KUL_YS) === 0
        || !!glb.default?.KULLANICI?.KUL_ISADMIN;
    const getDefaultGorusenRef = useCallback(() => {
        const currentKulRef = Number(glb.default?.KULLANICI?.KUL_REF || 0);
        const dataOwnerRef = Number(dataSahibiKulRef || 0);
        if (isGeneralYetkiUser) return currentKulRef > 0 ? currentKulRef : null;
        return null;
    }, [dataSahibiKulRef, isGeneralYetkiUser]);
    const canManageParaRecord = useCallback((row) => {
        if (!row || Number(row.CP_REF) <= 0) return true;
        return canDeleteByOwner(glb.default?.KULLANICI, tahsilatIsYapanRef(row));
    }, []);
    const musTemItems = useMemo(() => {
        const lookup = asArray(glb.default.KULLANICILkp);
        const base = filterKullanicilarByStatu(lookup, PARA_KUL_STATU.MUS_TEM);
        const allowed = allowedSatisYapanRefs({
            currentUser: Number(glb.default?.KULLANICI?.KUL_REF || 0),
            dataOwner: Number(dataSahibiKulRef || 0),
            isGeneralAuth: isGeneralYetkiUser,
        });
        if (allowed == null) return base;
        const picked = [];
        for (const ref of allowed) {
            const known = lookup.find((user) => Number(user?.KUL_REF) === ref) || base.find((user) => Number(user?.KUL_REF) === ref);
            picked.push(known || { KUL_REF: ref, KUL_ADI: ref === Number(glb.default?.KULLANICI?.KUL_REF) ? 'Siz' : 'Data Sahibi' });
        }
        const current = Number(activePara?.CP_MUS_TEM || 0);
        if (current > 0 && !picked.some((user) => Number(user.KUL_REF) === current)) {
            const existing = lookup.find((user) => Number(user?.KUL_REF) === current);
            if (existing) picked.push(existing);
        }
        return picked;
    }, [dataSahibiKulRef, isGeneralYetkiUser, activePara?.CP_MUS_TEM]);

    const totals = React.useMemo(() => {
        const borc = (paraList || []).reduce((s, r) => s + ((r?.CP_BA === true) ? (Number(r?.CP_TUTAR) || 0) : 0), 0);
        const alacak = (paraList || []).reduce((s, r) => s + ((r?.CP_BA === false) ? (Number(r?.CP_TUTAR) || 0) : 0), 0);
        const net = borc - alacak;
        return {
            borc, alacak, net,
            netLabel: net > 0 ? 'Net Borç' : net < 0 ? 'Net Alınan' : 'Net',
            netAbs: Math.abs(net)
        };
    }, [paraList]);
    const displayParaList = React.useMemo(() => {
        if (isCreatingNew && activePara && isFormRecordNew) return [...(paraList || []), activePara];
        return paraList || [];
    }, [paraList, isCreatingNew, activePara, isFormRecordNew]);

    const resolvedGridHeight = React.useMemo(() => {
        const maxFromProp = Number(gridHeight) > 0
            ? Number(gridHeight)
            : (Number(gridVisibleRowCount) > 0
                ? gridMaxHeightForRows(gridVisibleRowCount)
                : 500);
        return gridHeightByRowCount((displayParaList || []).length, maxFromProp);
    }, [gridHeight, gridVisibleRowCount, displayParaList]);

    return (
        <div ref={attachEnterNavRoot} style={{ width: '100%' }}>
            <div style={{
                marginBottom: '8px',
                fontWeight: 'bold',
                fontSize: '14px',
                color: 'var(--dx-color-text, #212529)',
                borderBottom: '2px solid var(--dx-color-primary, #1976d2)',
                paddingBottom: '4px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 8
            }}>
                <span>💰 {Dict.get('Borç/Alınan')}</span>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {formReadOnly && (
                        <>
                            <Button
                                text={Dict.get('Yeni Kayıt')}
                                icon="plus"
                                type="default"
                                disabled={paraSaving || !isParaListLoaded || paraReadOnly}
                                onClick={() => {
                                    const currentKulRef = glb.default?.KULLANICI?.KUL_REF ?? null;
                                    createLockRef.current = true;
                                    setFocusedParaKey(null);
                                    setNewFormToken(v => v + 1);
                                    setActivePara({
                                        CP_REF: -1,
                                        CP_TUR: null,
                                        CP_ODE_SEKLI: null,
                                        CP_BA: false, // varsayılan: Alınan
                                        CP_TUTAR: null,
                                        CP_TAR: new Date(),
                                        CP_ACK: '',
                                        CP_MUS_TEM: null,
                                        CP_GORUSEN: getDefaultGorusenRef(),
                                        CP_HEMSIRE: null,
                                        CP_EKIP: null,
                                        CP_REKLAMCI: null,
                                    });
                                    setFormReadOnly(false);
                                    setFormDirty(false);
                                    setIsCreatingNew(true);
                                }}
                            />
                            <Button
                                text={Dict.get('Düzenle')}
                                icon="edit"
                                type="default"
                                onClick={() => {
                                    if (!canManageParaRecord(activePara)) {
                                        notify('Bu tahsilatı düzenleme yetkiniz yok.', 'warning', 2500);
                                        return;
                                    }
                                    createLockRef.current = false;
                                    setFormReadOnly(false);
                                    setIsCreatingNew(false);
                                }}
                                disabled={paraSaving || !isParaListLoaded || paraReadOnly || !activePara || isActiveAgreement || !canManageParaRecord(activePara)}
                            />
                            <Button text={Dict.get('Yenile')} icon="refresh" type="default" onClick={() => { fetchParaList(true); setFormReadOnly(true); }} disabled={paraSaving || isNewUnsavedRecord} />
                            {activePara && Number(activePara.CP_REF) > 0 && !isActiveAgreement
                                && canDeleteByOwner(glb.default?.KULLANICI, tahsilatIsYapanRef(activePara)) && (
                                <Button text="Sil" icon="trash" type="danger" disabled={paraSaving} onClick={async () => {
                                    if (!window.confirm('Bu tahsilat silinsin mi?')) return;
                                    await savePara({ inserted: [], updated: [], deleted: [activePara.CP_REF] });
                                }} />
                            )}
                        </>
                    )}
                    {!formReadOnly && (
                        <>
                            <Button
                                text={Dict.get('Kaydet')}
                                icon="save"
                                type="success"
                                onClick={async () => {
                                    if (!activePara) return;
                                    if (isAgreementTur(activePara.CP_TUR)) {
                                        notify('Anlaşma Tutarı satırı manuel eklenemez/düzenlenemez/silinemez.', 'warning', 2500);
                                        return;
                                    }
                                    if (!activePara.CP_ODE_SEKLI) {
                                        notify('Ödeme Şekli zorunludur.', 'error', 2500);
                                        return;
                                    }
                                    if (!(Number(activePara.CP_TUTAR) > 0)) {
                                        notify('Tutar zorunludur ve sıfırdan büyük olmalıdır.', 'error', 2500);
                                        return;
                                    }
                                    if (!Number(activePara.CP_MUS_TEM)) {
                                        notify('Müşteri Temsilcisi zorunludur.', 'error', 2500);
                                        return;
                                    }
                                    const previousTem = (paraList || []).find((row) => Number(row?.CP_REF) === Number(activePara.CP_REF))?.CP_MUS_TEM;
                                    if (!canAssignSatisYapan({
                                        currentUser: Number(glb.default?.KULLANICI?.KUL_REF || 0),
                                        dataOwner: Number(dataSahibiKulRef || 0),
                                        isGeneralAuth: isGeneralYetkiUser,
                                    }, activePara.CP_MUS_TEM, Number(activePara.CP_REF) > 0 ? previousTem : null)) {
                                        notify('Müşteri temsilcisi yalnız siz veya data sahibi olabilir.', 'warning', 3000);
                                        return;
                                    }
                                    const tahsilatTs = activePara.CP_TAR ? new Date(activePara.CP_TAR).getTime() : 0;
                                    const satisTs = satisTarihi ? new Date(satisTarihi).getTime() : 0;
                                    if (Number.isFinite(tahsilatTs) && Number.isFinite(satisTs) && tahsilatTs < satisTs) {
                                        notify('Tahsilat tarihi satış tarihinden önce olamaz.', 'error', 3500);
                                        return;
                                    }
                                    if (Number(dataSahibiKulRef) > 0 && Number(activePara.CP_MUS_TEM) !== Number(dataSahibiKulRef)) {
                                        const devam = window.confirm('Data sahibinden farklı biri tahsilat yapıyor. Emin misiniz?');
                                        if (!devam) return;
                                    }
                                    const payloadRow = { ...activePara, CP_BA: resolveBorcluByTur(activePara.CP_TUR) };
                                    const changeGroups = isFormRecordNew
                                        ? { inserted: [payloadRow], updated: [], deleted: [] }
                                        : { inserted: [], updated: [payloadRow], deleted: [] };
                                    const success = await savePara(changeGroups);
                                    if (success) {
                                        if (Number(dataSahibiKulRef) > 0 && Number(activePara.CP_MUS_TEM) !== Number(dataSahibiKulRef)) {
                                            Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}report/temsilci_onay_talep`, {
                                                carRef, cpRef: Number(activePara.CP_REF) > 0 ? activePara.CP_REF : null,
                                                dataSahibiRef: dataSahibiKulRef, temsilciRef: activePara.CP_MUS_TEM,
                                                kulRef: glb.default.KULLANICI.KUL_REF,
                                            }).catch(() => null);
                                        }
                                        createLockRef.current = false;
                                        setFormReadOnly(true);
                                        setFormDirty(false);
                                        setIsCreatingNew(false);
                                    }
                                }}
                                disabled={paraSaving || !isParaListLoaded || (!isFormRecordNew && !formDirty)}
                            />
                            <Button text={Dict.get('Vazgeç')} icon="undo" type="warning" disabled={paraSaving} onClick={() => { createLockRef.current = false; fetchParaList(); setFormReadOnly(true); setFormDirty(false); setIsCreatingNew(false); }} />
                        </>
                    )}
                </div>
            </div>
            {isNewUnsavedRecord && (
                <div style={{ padding: 8, margin: '8px 0', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 4, color: '#856404' }}>
                    ℹ️ Borç/Alacak eklemek için önce satış kaydını kaydedin.
                </div>
            )}
            {activePara && (
                <Form
                    key={`para-form-${activePara?.CP_REF ?? 'new'}-${newFormToken}`}
                    formData={activePara}
                    readOnly={paraSaving || formReadOnly || paraReadOnly}
                    colCount={1}
                    onFieldDataChanged={(e) => {
                        setActivePara((prev) => {
                            let next = { ...prev, [e.dataField]: e.value };
                            if (e.dataField === 'CP_TUR') {
                                next.CP_BA = resolveBorcluByTur(e.value);
                            }
                            return next;
                        });
                        setFormDirty(true);
                    }}
                >
                    <GroupItem colCount={2}>
                        <SimpleItem
                            dataField="CP_TUR"
                            editorType="dxSelectBox"
                            editorOptions={{
                                items: (formReadOnly || paraReadOnly || isActiveAgreement) ? paraTurList : paraTurListSelectable,
                                searchEnabled: true,
                                searchMode: 'contains',
                                displayExpr: 'ST_AD',
                                searchExpr: 'ST_AD',
                                valueExpr: 'ST_KODINT'
                            }}
                        >
                            <Label text={Dict.get('Tür')} />
                        </SimpleItem>
                        <SimpleItem dataField="CP_ODE_SEKLI" editorType="dxSelectBox" editorOptions={{ items: filterArray(glb.default.sabitLkp, x => x.ST_TUR === 122), searchEnabled: true, searchMode: 'contains', displayExpr: 'ST_AD', searchExpr: 'ST_AD', valueExpr: 'ST_KODINT' }}><RequiredRule message="Ödeme Şekli zorunludur." /><Label text={Dict.get('Ödeme')} /></SimpleItem>
                        <SimpleItem dataField="CP_TUTAR" editorType="dxNumberBox" editorOptions={{ format: '#,##0.00', min: 0.01 }}><RequiredRule message="Tutar zorunludur." /><Label text={Dict.get('Tutar')} /></SimpleItem>
                        <SimpleItem
                            dataField="CP_EKIP"
                            editorType="dxSelectBox"
                            editorOptions={{
                                items: filterKullanicilarByStatu(asArray(glb.default.KULLANICILkp), PARA_KUL_STATU.EKIP),
                                searchEnabled: true,
                                searchMode: 'contains',
                                displayExpr: 'KUL_ADI',
                                searchExpr: 'KUL_ADI',
                                valueExpr: 'KUL_REF',
                                showClearButton: true
                            }}
                        >
                            <Label text={Dict.get('Ekip')} />
                        </SimpleItem>
                    </GroupItem>
                    <GroupItem colCount={2}>
                        <SimpleItem dataField="CP_TAR" editorType="dxDateBox" editorOptions={{ onOpened: (e) => injectQuickDatePresets(e?.component) }}><Label text={Dict.get('Tarih')} /></SimpleItem>
                        <SimpleItem dataField="CP_MUS_TEM" editorType="dxSelectBox" editorOptions={{ readOnly: Number(activePara?.CP_REF) > 0 && Number(activePara?.CP_MUS_TEM) > 0, items: musTemItems, searchEnabled: true, searchMode: 'contains', displayExpr: 'KUL_ADI', searchExpr: 'KUL_ADI', valueExpr: 'KUL_REF' }}><RequiredRule message="Müşteri Temsilcisi zorunludur." /><Label text={Dict.get('Müş.Tem.')} /></SimpleItem>
                        <SimpleItem dataField="CP_GORUSEN" editorType="dxSelectBox" editorOptions={{ readOnly: (glb.default.KULLANICI.KUL_YETKI !== 0), items: filterKullanicilarByStatu(asArray(glb.default.KULLANICILkp), PARA_KUL_STATU.GORUSEN), searchEnabled: true, searchMode: 'contains', displayExpr: 'KUL_ADI', searchExpr: 'KUL_ADI', valueExpr: 'KUL_REF' }}><Label text={Dict.get('Görüşmeci')} /></SimpleItem>
                        <SimpleItem dataField="CP_HEMSIRE" editorType="dxSelectBox" editorOptions={{ items: filterKullanicilarByStatu(asArray(glb.default.KULLANICILkp), PARA_KUL_STATU.HEMSIRE), searchEnabled: true, searchMode: 'contains', displayExpr: 'KUL_ADI', searchExpr: 'KUL_ADI', valueExpr: 'KUL_REF' }}><Label text={Dict.get('Hemşire')} /></SimpleItem>
                    </GroupItem>
                    <SimpleItem dataField="CP_ACK" editorType="dxTextArea" editorOptions={addMaxLengthToEditorOptions('CARI_PARA', 'CP_ACK', { rows: 3 })}><Label text={Dict.get('Açıklama')} /></SimpleItem>
                </Form>
            )}
            <DataGrid
                dataSource={displayParaList}
                keyExpr="CP_REF"
                showBorders={true}
                focusedRowEnabled={!isCreatingNew}
                focusedRowKey={isCreatingNew ? null : focusedParaKey}
                repaintChangesOnly={true}
                ref={gridRef}
                height={resolvedGridHeight}
                columnAutoWidth={false}
                columnMinWidth={120}
                onFocusedRowChanged={(e) => {
                    if (paraSaving || !formReadOnly || createLockRef.current) return;
                    const selected = normalizeParaUserSelections(e.row?.data || null);
                    if (selected) {
                        createLockRef.current = false;
                        setActivePara(selected);
                        setFocusedParaKey(selected.CP_REF);
                        setIsCreatingNew(false);
                    }
                }}
                onRowPrepared={(e) => {
                    if (e.rowType !== 'data' || !e.data) return;
                    const paintRow = (bg, color) => {
                        e.rowElement.style.setProperty('background-color', bg, 'important');
                        e.rowElement.style.setProperty('color', color, 'important');
                        e.rowElement.style.setProperty('font-weight', '700', 'important');
                        const cells = e.rowElement.querySelectorAll('td');
                        cells.forEach((cell) => {
                            cell.style.setProperty('background-color', bg, 'important');
                            cell.style.setProperty('color', color, 'important');
                            cell.style.setProperty('font-weight', '700', 'important');
                        });
                    };

                    if (isAgreementTur(e.data.CP_TUR)) {
                        // Kullanıcı talebi: Anlaşma satırının mevcut görünümüne dokunma.
                        e.rowElement.style.backgroundColor = '#ffe5e5';
                        e.rowElement.style.color = '#b00020';
                        return;
                    }

                    const isBorc = e.data.CP_BA === true || e.data.CP_BA === 1 || e.data.CP_BA === '1';
                    const isAlinan = e.data.CP_BA === false || e.data.CP_BA === 0 || e.data.CP_BA === '0';
                    if (isBorc) {
                        paintRow('#d32f2f', '#ffffff');
                        return;
                    }
                    if (isAlinan) {
                        paintRow('#2e7d32', '#ffffff');
                    }
                }}
            >
                <Paging enabled={false} />
                <Scrolling mode="standard" useNative={false} showScrollbar="always" columnRenderingMode="standard" />
                <LoadPanel enabled={true} />
                <GridToolbar>
                    <GridToolbarItem location="before" render={() => (
                        <div style={{ padding: '2px 6px', borderRadius: 4, background: 'rgba(0,0,0,0.04)', fontSize: 12 }}>
                            <b>{Dict.get('Toplam Borç')}:</b> {formatMoney(totals.borc)}
                        </div>
                    )} />
                    <GridToolbarItem location="before" render={() => (
                        <div style={{ padding: '2px 6px', borderRadius: 4, background: 'rgba(0,0,0,0.04)', fontSize: 12 }}>
                            <b>{Dict.get('Toplam Alınan')}:</b> {formatMoney(totals.alacak)}
                        </div>
                    )} />
                    <GridToolbarItem location="before" render={() => (
                        <div style={{
                            padding: '2px 6px',
                            borderRadius: 4,
                            background: totals.net > 0 ? 'rgba(211,47,47,0.08)' : totals.net < 0 ? 'rgba(46,125,50,0.08)' : 'rgba(0,0,0,0.04)',
                            color: totals.net > 0 ? '#d32f2f' : totals.net < 0 ? '#2e7d32' : '#555',
                            fontSize: 12
                        }}>
                            <b>{totals.netLabel}:</b> {formatMoney(totals.netAbs)}
                        </div>
                    )} />
                </GridToolbar>
                <Column dataField="CP_REF" visible={false} allowEditing={false} caption={Dict.get('K.No')} />
                <Column dataField="CP_TUR"  width={100} caption={Dict.get('Tür')}>
                    <Lookup
                        dataSource={paraTurList}
                        searchEnabled={true}
                        searchMode='contains'
                        displayExpr={(item) => {
                            if (!item) return '';
                            const ad = String(item.ST_AD || '').toLocaleLowerCase('tr-TR');
                            return (ad.includes('anlaş') || ad.includes('anlas')) ? 'Anlaşma Tutarı' : item.ST_AD;
                        }}
                        searchExpr='ST_AD'
                        valueExpr='ST_KODINT'
                    />
                </Column>
                <Column dataField="CP_ODE_SEKLI" caption={Dict.get('Ödeme')}>
                    <Lookup dataSource={filterArray(glb.default.sabitLkp, x => x.ST_TUR === 122)} searchEnabled={true} searchMode='contains' displayExpr='ST_AD' searchExpr='ST_AD' valueExpr='ST_KODINT' />
                </Column>
                <Column dataField="CP_TUTAR" caption={Dict.get('Tutar')} dataType='number' format={'#,##0.00'} />
                <Column dataField="CP_TAR" caption={Dict.get('Tarih')} dataType='date' format='dd-MM-yyyy' />
                <Column {...addMaxLengthToGridColumn('CARI_PARA', 'CP_ACK', { dataField: "CP_ACK", caption: Dict.get('Açıklama') })} />
                <Column dataField='CP_MUS_TEM' caption={Dict.get('Müş.Tem.')}>
                    <Lookup dataSource={asArray(glb.default.KULLANICILkp)}
                        searchEnabled={true} searchMode='contains' displayExpr='KUL_ADI' searchExpr='KUL_ADI' valueExpr='KUL_REF' />
                </Column>
                <Column dataField='CP_GORUSEN' caption={Dict.get('Görüşmeci')}>
                    <Lookup dataSource={asArray(glb.default.KULLANICILkp)}
                        searchEnabled={true} searchMode='contains' displayExpr='KUL_ADI' searchExpr='KUL_ADI' valueExpr='KUL_REF' />
                </Column>
                <Column dataField='CP_HEMSIRE' caption={Dict.get('Hemşire')}>
                    <Lookup dataSource={asArray(glb.default.KULLANICILkp)}
                        searchEnabled={true} searchMode='contains' displayExpr='KUL_ADI' searchExpr='KUL_ADI' valueExpr='KUL_REF' />
                </Column>
                <Column dataField='CP_EKIP' caption={Dict.get('Ekip')}>
                    <Lookup dataSource={asArray(glb.default.KULLANICILkp)}
                        searchEnabled={true} searchMode='contains' displayExpr='KUL_ADI' searchExpr='KUL_ADI' valueExpr='KUL_REF' />
                </Column>
                <Column dataField="CP_BA" caption={Dict.get('Borç/Alınan')}>
                    <Lookup dataSource={[{ value: true, text: 'Borç' }, { value: false, text: 'Alınan' }]} displayExpr='text' valueExpr='value' />
                </Column> 
                {/* <Column dataField="CP_CI_REF" caption={Dict.get('İşlem Ref')}>
                    <Lookup dataSource={islemList} displayExpr='CI_REF' valueExpr='CI_REF' />
                </Column> */}
            </DataGrid>
        </div>
    );
}
